// assets/custom-product-grid.js

(function () {
  'use strict';

  const SELECTORS = {
    root:       '[data-component="hbk-product-grid"]',
    filterChip: '[data-component="collection-filter"] .filter-chips__link',
    sortSelect: '[data-sort-select]',
    pageLink:   '[data-page-link]',
    gridItems:  '.hbk-pgrid__grid > li',
    countEl:    '.hbk-pgrid__count',
  };

  class ProductGrid {
    constructor(el) {
      this.el               = el;
      this.sectionId        = el.dataset.sectionId;
      this.collectionHandle = el.dataset.collection || '';
      this._busy            = false;
      this._init();
    }

    _init() {
      this._bindSort();
      this._bindPopstate();
      // Apply any filter param already in the page URL on load
      this._applyClientFilter(window.location.href);
    }

    // ── Filter config from mega panel data ───────────────────────────────────

    _getFilterConfigs() {
      if (!this.collectionHandle) return [];
      try {
        const esc      = CSS.escape(this.collectionHandle);
        const configEl = document.querySelector(`.mfb-col-config-filter[data-collection="${esc}"]`);
        if (!configEl) return [];
        return JSON.parse(configEl.textContent) || [];
      } catch { return []; }
    }

    // ── Client-side product filtering ────────────────────────────────────────
    // Uses data-fi-N attributes stamped onto each <li> by the Liquid template.
    // These attributes mirror the mega panel's product pool approach exactly.

    _applyClientFilter(url = window.location.href) {
      const params  = new URL(url, location.origin).searchParams;
      const configs = this._getFilterConfigs();

      // Find which filter slot is active by matching URL param to config
      let activeConfig = null;
      let activeValue  = null;

      for (const f of configs) {
        if (!f.label) continue;
        const isTag    = !f.ns || f.ns === 'tag';
        const paramKey = isTag ? 'filter.p.tag' : `filter.p.m.${f.ns}.${f.key}`;
        const paramVal = params.get(paramKey);
        if (paramVal !== null) {
          activeConfig = f;
          activeValue  = paramVal.toLowerCase();
          break;
        }
      }

      const items = this.el.querySelectorAll(SELECTORS.gridItems);
      let visibleCount = 0;

      items.forEach(item => {
        if (!activeConfig) {
          item.hidden = false;
          visibleCount++;
          return;
        }

        const isTag = !activeConfig.ns || activeConfig.ns === 'tag';

        // Source 1: data-fi-N on <li> — stamped by Liquid when mega_block collection
        //           match succeeds via sections['header'].blocks lookup.
        // Source 2: data-mf-{ns}-{key} or data-product-tags on <li> — stamped by Liquid
        //           from ALL header mega_panel blocks (no collection match required).
        // Source 3: same attributes on .hbk-at-card — hardcoded fallback in card snippet.
        let attrVal = '';
        const fi = item.getAttribute(`data-fi-${activeConfig.index}`);
        if (fi !== null) {
          attrVal = fi;
        } else if (isTag) {
          attrVal = item.getAttribute('data-product-tags')
            ?? item.querySelector('.hbk-at-card')?.getAttribute('data-product-tags')
            ?? '';
        } else {
          const mfAttr = `data-mf-${activeConfig.ns}-${activeConfig.key}`;
          attrVal = item.getAttribute(mfAttr)
            ?? item.querySelector('.hbk-at-card')?.getAttribute(mfAttr)
            ?? '';
        }

        const matches = attrVal.split('|').some(v => v.trim() === activeValue);
        item.hidden   = !matches;
        if (matches) visibleCount++;
      });

      // Update visible product count
      const countEl = this.el.querySelector(SELECTORS.countEl);
      if (countEl) {
        if (activeConfig !== null) {
          if (!countEl.dataset.originalText) {
            countEl.dataset.originalText = countEl.textContent.trim();
          }
          countEl.textContent = `${visibleCount} Produkte`;
        } else if (countEl.dataset.originalText) {
          countEl.textContent = countEl.dataset.originalText;
          delete countEl.dataset.originalText;
        }
      }
    }

    // ── AJAX grid update (sort, pagination, popstate) ────────────────────────

    async _updateGrid(targetUrl) {
      if (this._busy) return;
      this._busy = true;
      this.el.setAttribute('aria-busy', 'true');

      const url = new URL(targetUrl, window.location.origin);
      url.searchParams.set('sections', this.sectionId);

      try {
        const res = await fetch(url.toString(), { headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const html = data[this.sectionId];
        if (!html) throw new Error('No section HTML in response');

        const doc        = new DOMParser().parseFromString(html, 'text/html');
        const newSection = doc.getElementById(`shopify-section-${this.sectionId}`);
        if (!newSection) throw new Error('Section element missing in response');

        const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);
        if (currentSection) {
          currentSection.replaceWith(newSection);
          this.el = newSection.querySelector(SELECTORS.root) ?? newSection;
          this._bindSort();
          newSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        history.pushState({}, '', targetUrl);
        this._syncChipStates(targetUrl);
        // Re-apply client-side filter after the new page's products are in the DOM
        this._applyClientFilter(targetUrl);

      } catch (err) {
        console.error('[HBK Product Grid] Update failed:', err);
        window.location.href = targetUrl;
      } finally {
        this._busy = false;
        this.el.removeAttribute('aria-busy');
      }
    }

    // ── Sync filter chip active states after URL change ───────────────────────

    _syncChipStates(targetUrl) {
      const params = new URL(targetUrl, window.location.origin).searchParams;

      document.querySelectorAll(SELECTORS.filterChip).forEach(chip => {
        const ns    = chip.dataset.filterNs    || '';
        const key   = chip.dataset.filterKey   || '';
        const value = chip.dataset.filterValue || '';
        const isTag = chip.dataset.isTag === 'true';

        let active = false;
        if (value) {
          if (isTag || !ns || ns === 'tag') {
            active = params.get('filter.p.tag') === value;
          } else {
            active = params.get(`filter.p.m.${ns}.${key}`) === value;
          }
        }

        chip.classList.toggle('is-active', active);
        if (active) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });
    }

    // ── Sort dropdown ─────────────────────────────────────────────────────────

    _bindSort() {
      const select = this.el.querySelector(SELECTORS.sortSelect);
      if (!select) return;
      select.addEventListener('change', () => {
        // Preserve any active filter params from the current URL
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        this._updateGrid(url.toString());
      });
    }

    // ── Browser back/forward ──────────────────────────────────────────────────

    _bindPopstate() {
      window.addEventListener('popstate', () => this._updateGrid(window.location.href));
    }

    destroy() {}
  }

  // Singleton (one product grid per collection page)
  let instance = null;

  function init(scope = document) {
    const el = scope.querySelector
      ? scope.querySelector(SELECTORS.root)
      : scope;
    if (!el || instance) return;
    instance = new ProductGrid(el);
  }

  function destroy() {
    instance?.destroy();
    instance = null;
  }

  // ── Merge current filter params into a URL ────────────────────────────────
  function _mergeFilterParams(linkHref) {
    const url     = new URL(linkHref, location.origin);
    const current = new URL(window.location.href, location.origin).searchParams;
    for (const [key, value] of current.entries()) {
      if (key.startsWith('filter.p.')) url.searchParams.set(key, value);
    }
    return url.toString();
  }

  // ── Global delegated event listeners ─────────────────────────────────────

  // Filter chip clicks → client-side filter (no AJAX).
  // Falls back to AJAX if no mfb-col-config-filter found on page
  // (which requires Storefront Filters to be enabled in Shopify admin).
  document.addEventListener('click', e => {
    const chip = e.target.closest(SELECTORS.filterChip);
    if (!chip || !chip.href) return;
    if (!instance) return;
    e.preventDefault();

    const configs = instance._getFilterConfigs();
    if (configs.length > 0) {
      history.pushState({}, '', chip.href);
      instance._syncChipStates(chip.href);
      instance._applyClientFilter(chip.href);
      // Notify CollectionFilter JS so it can update the current-filter display
      document.dispatchEvent(new CustomEvent('hbk:filter:applied', { detail: { url: chip.href } }));
    } else {
      // No filter config — fall back to AJAX (requires Storefront Filters in admin)
      instance._updateGrid(chip.href);
    }
  });

  // [data-page-link] pagination — merge existing filter params before AJAX
  document.addEventListener('click', e => {
    const link = e.target.closest(SELECTORS.pageLink);
    if (!link || !link.href) return;
    if (!instance) return;
    e.preventDefault();
    instance._updateGrid(_mergeFilterParams(link.href));
  });

  // Dawn's default_pagination links inside the grid section — merge filter params
  document.addEventListener('click', e => {
    if (!instance) return;
    const section = document.getElementById(`shopify-section-${instance.sectionId}`);
    if (!section) return;
    const link = e.target.closest('a[href]');
    if (!link || !section.contains(link)) return;
    if (link.closest('.hbk-pgrid__pagination') || link.closest('.hbk-pgrid__load-more')) {
      e.preventDefault();
      instance._updateGrid(_mergeFilterParams(link.href));
    }
  });

  // ATC handler for grid cards
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-at-atc]');
    if (!btn) return;
    const gridSection = document.getElementById(`shopify-section-${instance?.sectionId}`);
    if (!gridSection?.contains(btn)) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const original = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    try {
      await window.MFBCart?.add({ id: variantId, quantity: 1 });
      btn.textContent = '✓';
      setTimeout(() => { btn.textContent = original; }, 1800);
    } catch {
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = original; }, 1800);
    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  });

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', () => destroy());

})();
